/**
 * Ingest monitoring read, its preset builder, and the failure taxonomy.
 */
import type { AxiosResponse } from "axios";
import axiosClient from "@/shared/lib/axios/axios.ts";
import { API_ROUTES } from "@/alerts/api/config.ts";
import {
  unwrapEnvelope,
  toAlertApiError,
  type ApiEnvelope,
} from "@/alerts/api/envelope.ts";
import { isAlertsMockMode } from "@/alerts/api/mocks/alerts.mocks.ts";
import { mockAlertMonitoring } from "@/alerts/api/mocks/alerts-monitoring.mocks.ts";
import {
  MONITORING_PRESETS,
  SEVEN_DAYS_SECONDS,
  type AlertMonitoringParams,
  type AlertMonitoringResponse,
  type AlertMonitoringScopeInput,
  type MonitoringPreset,
} from "@/alerts/types/alerts-monitoring.ts";
import i18n from "@/shared/lib/i18n/index.ts";
import { activeLocale, formatNumber } from "@/shared/lib/i18n/formatters.ts";

/** The backend rejects a monitoring window wider than seven days, exactly. */
export const MONITORING_MAX_RANGE_MS = SEVEN_DAYS_SECONDS * 1000;

/** Whether a range would have to be narrowed to be accepted. */
export const monitoringRangeExceedsMax = (from: Date, to: Date): boolean =>
  to.getTime() - from.getTime() > MONITORING_MAX_RANGE_MS;

/**
 * Build request params from an explicit [from, to] window — the global date
 * picker, rather than a monitoring-only preset.
 *
 * Both boundaries are sent, and both as ISO-8601 with an explicit `Z`: the
 * alerts API rejects the naive `YYYY-MM-DD HH:mm:ss` the machines module emits.
 *
 * The window is CLAMPED to seven days ending at `to`, because the backend
 * enforces that maximum and answers a wider request with a validation error
 * rather than a truncated result. Clamping keeps the most recent seven days,
 * which is the half an operator looking at ingest health actually wants; callers
 * can use {@link monitoringRangeExceedsMax} to say so on screen rather than
 * silently showing a narrower window than the picker claims.
 *
 * Unlike the preset builder there is no `Date.now()` here at all, so the query
 * key changes only when the picker does — the churn that once fired six requests
 * in three seconds is structurally impossible on this path.
 */
export const buildMonitoringRangeParams = (
  from: Date,
  to: Date,
  scope: AlertMonitoringScopeInput = {},
): AlertMonitoringParams => {
  const end = to.getTime();
  const start = Math.max(from.getTime(), end - MONITORING_MAX_RANGE_MS);
  return {
    ...scope,
    start_time: new Date(start).toISOString(),
    end_time: new Date(end).toISOString(),
  };
};

/**
 * Build request params from a rolling preset.
 *
 * - 1h / 6h / 24h send `start_time` ONLY. The backend resolves the end from its
 *   own `as_of`, so a skewed browser clock cannot shift the window.
 * - 7d is the exception: it must anchor on the last successful `as_of`, because
 *   the backend enforces an exact seven-day maximum and a client clock running
 *   even slightly fast would push `as_of - start_time` over the limit and get
 *   the request rejected. With no anchor yet (first load), the start is pulled
 *   in by five minutes and `end_time` is omitted — deliberately asking for
 *   slightly less than seven days rather than slightly more.
 */
export const buildMonitoringParams = (
  preset: MonitoringPreset,
  options: { anchorAsOf?: string | null; scope?: AlertMonitoringScopeInput; now?: number } = {},
): AlertMonitoringParams => {
  const scope = options.scope ?? {};
  const now = options.now ?? Date.now();

  if (preset === "7d") {
    const anchorMs = options.anchorAsOf ? Date.parse(options.anchorAsOf) : NaN;
    if (Number.isFinite(anchorMs)) {
      return {
        ...scope,
        start_time: new Date(anchorMs - SEVEN_DAYS_SECONDS * 1000).toISOString(),
        end_time: new Date(anchorMs).toISOString(),
      };
    }
    return {
      ...scope,
      start_time: new Date(now - (SEVEN_DAYS_SECONDS - 300) * 1000).toISOString(),
    };
  }

  const seconds = MONITORING_PRESETS.find((p) => p.id === preset)?.seconds ?? 21_600;
  return { ...scope, start_time: new Date(now - seconds * 1000).toISOString() };
};

/**
 * How a monitoring failure must be handled. Each kind has a different recovery,
 * and treating them alike would either retry an invalid request forever or
 * zero-fill a dashboard that has no data to show.
 */
export type MonitoringErrorKind =
  /** 403 — hide the staff surface; never show an empty dashboard instead. */
  | { kind: "forbidden" }
  /** 429 — keep the last good data and wait out Retry-After. */
  | { kind: "throttled"; retryAfterSeconds: number }
  /** 400 invalid ordering — rebuild the preset rather than retrying as-is. */
  | { kind: "recompute_preset" }
  /** 422 out of bounds — clamp back to the seven-day maximum. */
  | { kind: "clamp_range" }
  /** 422 cohort too large — ask the user to narrow period or scope. */
  | { kind: "narrow_scope" }
  /** 503 bad configuration — unavailable, and NOT the same as zero data. */
  | { kind: "unavailable" }
  | { kind: "generic"; message: string };

export const classifyMonitoringError = (error: unknown): MonitoringErrorKind => {
  const normalized = toAlertApiError(error);
  if (normalized.status === 403) return { kind: "forbidden" };
  if (normalized.status === 429) {
    return { kind: "throttled", retryAfterSeconds: normalized.retryAfterSeconds ?? 30 };
  }
  switch (normalized.code) {
    case "invalid_time_range_order":
      return { kind: "recompute_preset" };
    case "time_range_out_of_bounds":
      return { kind: "clamp_range" };
    case "alert_monitoring_cohort_limit_exceeded":
      return { kind: "narrow_scope" };
    case "alert_monitoring_configuration_invalid":
      return { kind: "unavailable" };
    default:
      return {
        kind: "generic",
        message: normalized.code
          ? `${normalized.message} (${normalized.code})`
          : normalized.message,
      };
  }
};

const csv = (values?: Array<string | number>): string | undefined =>
  values && values.length > 0 ? values.join(",") : undefined;

const toQuery = (params: AlertMonitoringParams): Record<string, unknown> => {
  const query: Record<string, unknown> = {
    start_time: params.start_time,
    end_time: params.end_time,
    organization_id: params.organization_id,
    location_id: params.location_id,
    machine_ids: csv(params.machine_ids),
    device_mxids: csv(params.device_mxids),
    source_components: csv(params.source_components),
    type_in: csv(params.type_in),
  };
  Object.keys(query).forEach((key) => query[key] === undefined && delete query[key]);
  return query;
};

/** GET /api/monitoring/alerts/ — staff-only. */
export const getAlertMonitoring = async (
  params: AlertMonitoringParams,
  signal?: AbortSignal,
): Promise<AlertMonitoringResponse> => {
  if (isAlertsMockMode()) return mockAlertMonitoring(params);
  try {
    const response = await axiosClient.get<
      unknown,
      AxiosResponse<ApiEnvelope<AlertMonitoringResponse, unknown>>
    >(API_ROUTES.ALERTS_MONITORING, { params: toQuery(params), signal });
    return unwrapEnvelope(response).data;
  } catch (error) {
    throw toAlertApiError(error);
  }
};

/* ------------------------------------------------------------------ *
 * Bucket formatting
 * ------------------------------------------------------------------ */

/**
 * Human bucket size from `bucket_seconds` — "1-day", "1-hour", "5-min".
 *
 * Read from the field, never inferred from whether a timestamp happens to land
 * on `00:00Z`: a daily bucket aligned to a location's local midnight starts at
 * some other UTC hour, and guessing from the clock would silently mislabel it.
 */
export const formatBucketSize = (bucketSeconds: number): string => {
  if (bucketSeconds >= 86_400) {
    return i18n.t("bucketSize.day", { ns: "alerts", count: Math.round(bucketSeconds / 86_400) });
  }
  if (bucketSeconds >= 3_600) {
    return i18n.t("bucketSize.hour", { ns: "alerts", count: Math.round(bucketSeconds / 3_600) });
  }
  const minutes = Math.max(1, Math.round(bucketSeconds / 60));
  return i18n.t("bucketSize.minute", { ns: "alerts", count: minutes });
};

/** Daily buckets get a date tick; anything shorter gets a time tick. */
export const isDailyBucket = (bucketSeconds: number): boolean => bucketSeconds >= 86_400;

export const formatBucketTick = (iso: string, bucketSeconds: number): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  // Ticks are UTC by contract (the whole page is on the accepted-time basis), so
  // the zone is pinned and only the language varies. `en-GB` keeps the English
  // ticks day-first; the month is a name, so neither order can be misread.
  const locale = activeLocale() === "pt-BR" ? "pt-BR" : "en-GB";
  return isDailyBucket(bucketSeconds)
    ? new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "short",
        timeZone: "UTC",
      }).format(date)
    : new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "UTC",
      }).format(date);
};

/** Latency for display: sub-second in ms, above that in seconds. */
export const formatLatency = (ms: number | null): string => {
  if (ms == null) return "—";
  if (ms < 1000) return `${formatNumber(Math.round(ms))}ms`;
  // pt-BR decimalises with a comma, so the seconds figure goes through Intl
  // rather than `toFixed`, which always emits a dot.
  const seconds = formatNumber(ms / 1000, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return `${seconds}s`;
};
